/**
 * <TutorGenerateActivityFlow> — « Générer une activité », du côté du tuteur (template).
 *
 * `GenerateActivityModal.js` dans son usage d'origine : le tuteur génère pour UN élève (« Générer
 * une activité » sur sa liste d'activités) ou pour PLUSIEURS (« Activité » dans « Mes élèves » :
 * « Activité commune »). C'est `<GenerateActivityFlow>`, que le parent et l'élève ont déjà, avec
 * ce que le tuteur a de plus — et rien d'autre :
 *  - la voix : le tuteur est tutoyé (`voice="student"`, les `TEXTS.student` du legacy) ;
 *  - le cadrage sans Classe ni Matière à l'étape du formulaire (`hideClassSubject={!imageMode}`
 *    dans le legacy : la classe et la matière viennent de l'élève, ou du chapitre choisi) ; elles
 *    reviennent à l'étape du fichier, où l'analyse peut les corriger ;
 *  - pour plusieurs élèves, le titre « Activité commune », leur rangée en tête de chaque étape —
 *    leurs visages, combien ils sont, « Modifier » — et le choix des élèves
 *    (`<TutorStudentPickerModal>`), ouvert d'emblée depuis « Mes élèves » ;
 *  - le « Mode programme de révision », quand un élève visé a un programme (`studyProgram`) ;
 *  - « Générer à partir de l'exo « … » » (la fiche d'une activité) : un titre à soi (`formTitle`),
 *    Type, Durée et Instructions seulement, sans fichier ; quand le chapitre de l'exo n'est pas fiable, le
 *    champ Chapitre en tête et l'avertissement qui le dit (`notice`), ou « Générer sans chapitre »
 *    (`submitLabel`) quand sa matière n'en a aucun dans la classe.
 *
 * Un écart de mots reste à trancher : la chip des centres d'intérêt dit « tes centres d'intérêt »
 * dans la voix de l'élève, alors que ce sont ceux de l'élève visé (le legacy disait seulement
 * « Personnaliser ») — une voix du tuteur est proposée dans l'audit de parité.
 *
 * Contrôlé, comme le gabarit qu'il habille.
 */
import React from "react"

import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { AvatarStack } from "../../molecules/AvatarStack"
import { GenerateActivityFlow, type GenerateActivityFlowProps } from "../GenerateActivityFlow/GenerateActivityFlow"
import { TutorStudentPickerModal, type TutorStudentPickerModalProps } from "../TutorStudentPickerModal"
import { type UIActivityGenerationField } from "../../../ports/primary/ui/types"
import { type UITutorStudent } from "../../../ports/primary/ui/types/tutorEleves"
import { countLabel, joinFrench } from "../../../ports/primary/ui/formatters/textFormatter"

/** Le cadrage du tuteur au formulaire : tout, sauf Classe et Matière. */
const TUTOR_GENERATION_FIELDS: UIActivityGenerationField[] = [
    "chapter",
    "skillParts",
    "level",
    "duration",
    "instructions",
]

/** « Jules M., Léa B. et 3 autres » : deux noms au plus, puis le reste compté. */
function recipientsLabel(students: UITutorStudent[]): string {
    const names = students.slice(0, 2).map((student) => student.name)
    const others = students.length - names.length
    return joinFrench(others > 0 ? [...names, countLabel(others, "autre")] : names)
}

export type TutorGenerateActivityFlowProps = Omit<GenerateActivityFlowProps, "voice" | "lead"> & {
    /**
     * Les élèves d'une activité commune. Absent : une activité pour un seul élève — ni rangée, ni
     * « Activité commune ».
     */
    targetStudents?: UITutorStudent[]
    /** « Modifier » sur la rangée : rouvre le choix des élèves. */
    onEditTargetStudents?: () => void
    /** Le choix des élèves (une activité commune). Absent : pas de choix. */
    picker?: Omit<TutorStudentPickerModalProps, "testID"> | null
}

export function TutorGenerateActivityFlow(props: TutorGenerateActivityFlowProps) {
    const {
        targetStudents,
        onEditTargetStudents,
        picker,
        formFields = TUTOR_GENERATION_FIELDS,
        formTitle,
        ...flow
    } = props
    const common = targetStudents != null

    const lead =
        common && targetStudents.length > 0 ? (
            <Row gap="3" align="center" justify="between">
                <AvatarStack
                    people={targetStudents.map((student) => ({
                        id: student.id,
                        uri: student.avatarUrl,
                        name: student.name,
                    }))}
                    max={3}
                    label={recipientsLabel(targetStudents)}
                />
                {onEditTargetStudents != null && <TextButton label="Modifier" onPress={onEditTargetStudents} />}
            </Row>
        ) : undefined

    return (
        <>
            <GenerateActivityFlow
                {...flow}
                // Le choix des élèves se pose À LA PLACE de la feuille, comme la modale du legacy
                // par-dessus la sienne : deux feuilles empilées se disputeraient le portail.
                visible={flow.visible && picker?.visible !== true}
                formFields={formFields}
                voice="student"
                // Un titre donné l'emporte : « Générer à partir de l'exo « … » » de la fiche d'une
                // activité (`ParcoursChoiceModal.js`) ; sinon « Activité commune » pour plusieurs élèves.
                formTitle={formTitle ?? (common ? "Activité commune" : undefined)}
                lead={lead}
            />
            {picker != null && <TutorStudentPickerModal {...picker} />}
        </>
    )
}
